/**
 * Logger utility for consistent logging across the application
 * Only shows logs in development mode by default
 */

import * as Sentry from "@sentry/vue";

// Check if Sentry DSN is available
const sentryEnabled = !!import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.MODE || 'development';
const isDebug = environment !== 'production';

/**
 * Global logging utility with Sentry integration
 */
export const logger = {
  /**
   * Debug level logging - hidden in production
   */
  debug(...args) {
    if (!isDebug) return;
    console.debug('[DEBUG]', ...args);
  },

  /**
   * Standard logging
   */
  log(...args) {
    console.log(...args);
  },

  /**
   * Log an informational message
   */
  info(message, data = {}) {
    if (isDebug) {
      console.info(`[INFO] ${message}`, data);
    }
  },

  /**
   * Log a warning message and send to Sentry as breadcrumb
   */
  warn(message, data = {}) {
    console.warn(`[WARN] ${message}`, data);
    
    // Add breadcrumb in Sentry
    if (sentryEnabled) {
      Sentry.addBreadcrumb({
        category: 'warning',
        message,
        level: 'warning',
        data
      });
    }
  },

  /**
   * Log an error message and capture in Sentry
   */
  error(error, context = {}, captureException = true) {
    const errorMsg = error instanceof Error ? error.message : error;
    console.error(`[ERROR] ${errorMsg}`, context, error instanceof Error ? error : '');
    
    // Report to Sentry
    if (sentryEnabled && captureException) {
      if (error instanceof Error) {
        Sentry.captureException(error, { 
          extra: context
        });
      } else {
        Sentry.captureMessage(errorMsg, {
          level: 'error',
          extra: context
        });
      }
    }
  },

  /**
   * Track a specific event for analytics and monitoring
   */
  trackEvent(eventName, data = {}) {
    this.info(`Event: ${eventName}`, data);
    
    // Track in Sentry if enabled
    if (sentryEnabled) {
      Sentry.addBreadcrumb({
        category: 'event',
        message: eventName,
        level: 'info',
        data
      });
    }
  },

  /**
   * Set user information for error tracking
   */
  setUser(user) {
    if (sentryEnabled && user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.username,
      });
    }
  },

  /**
   * Clear user information on logout
   */
  clearUser() {
    if (sentryEnabled) {
      Sentry.setUser(null);
    }
  },

  /**
   * Start monitoring a performance transaction
   */
  startTransaction(name, op) {
    if (sentryEnabled) {
      return Sentry.startTransaction({ name, op });
    }
    return null;
  }
};

export default logger; 